import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Store as NgrxStore } from "@ngrx/store";
import { VisitTypeResourceStore } from "@app/services/store/visit-type-resource-store";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { UntypedFormBuilder } from "@angular/forms";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { VisitTypeResource } from "@app/models/rest/visit-type-resource";
import { VisitType } from "@app/models/rest/visit-type";
import { HateoasResourceService } from "@lagoshny/ngx-hateoas-client";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { CustomAjaxStore } from "@app/view/shared/base-pages/base-data-table-page/custom-ajax-store.component";
import { VisitTypeSkillProfileAddComponent } from "@app/view/setup/assignment/visit-type-skill-profile-add/visit-type-skill-profile-add.component";
import { GridComponent } from "@app/view/shared/base-pages/base-data-table-page/grid.component";
import { MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import {
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import { TranslatePipe } from "@ngx-translate/core";
import { MatChipSet, MatChip } from "@angular/material/chips";

@Component({
  selector: "app-visit-type-resource-grid",
  templateUrl: "./visit-type-resource-grid.component.html",
  styleUrls: ["./visit-type-resource-grid.component.scss"],
  imports: [
    GridComponent,
    MatButton,
    MatIcon,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    TranslatePipe,
    MatChipSet,
    MatChip
  ]
})
export class VisitTypeResourceGridComponent
  extends BaseDataTablePageDirective<VisitTypeResource>
  implements OnInit
{
  visitTypeResourceStore: VisitTypeResourceStore;
  protected store: NgrxStore<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;
  private hateoasResourceService = inject(HateoasResourceService);
  private clientConfigService = inject(ClientConfigService);

  @Input() visitType: VisitType;
  @Output() entityChange = new EventEmitter<any>();

  @Input()
  public columns: object[] = [
    {
      field: "skillProfile",
      text: "Skill Profile",
      flex: 2,
      renderer: ({ record }) => {
        return record.skillProfile
          ? typeof record.skillProfile === "string"
            ? record.skillProfile
            : record.skillProfile.description
          : "Unknown";
      },
      editor: false,
      editable: false
    },
    {
      field: "hideInspection",
      text: "Hide Inspection",
      type: "check",
      flex: 1,
      align: "center"
    },
    {
      field: "hideSchedules",
      text: "Hide Schedules",
      type: "check",
      flex: 1,
      align: "center"
    },
    {
      field: "hideMaterials",
      text: "Hide Materials",
      type: "check",
      flex: 1,
      align: "center"
    }
  ];

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const visitTypeResourceStore = inject(VisitTypeResourceStore);
    const store = inject<NgrxStore<any>>(NgrxStore);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      visitTypeResourceStore,
      store,
      dialog,
      "VisitTypeResource",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );
    this.visitTypeResourceStore = visitTypeResourceStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
    const hateoasResourceService = this.hateoasResourceService;
    const clientConfigService = this.clientConfigService;

    // Set the required services on the CustomAjaxStore class
    CustomAjaxStore.setClientConfigService(clientConfigService);
    CustomAjaxStore.setHateoasResourceService(hateoasResourceService);
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  get resourceCount(): number {
    return this.gridStore?.count || 0;
  }

  ngOnInit() {
    super.ngOnInit();

    // Set autoCommit to false before loading data
    this.gridStore.autoCommit = false;

    // Set up filter for visitType and detail projection
    if (this.visitType) {
      this.gridStore.fixedFilters = [
        {
          property: "visitType.id",
          operator: "Equals",
          value: this.visitType.id
        }
      ];

      // Pass projection directly in the load parameters
      this.gridStore.load({
        params: {
          projection: "detail"
        }
      });
    }
  }

  addResource(): void {
    const dialogRef = this.dialog.open(VisitTypeSkillProfileAddComponent, {
      data: { resource: this.visitType }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Reload data after dialog closes
        this.gridStore.load({
          params: {
            projection: "detail"
          }
        });
      }
    });
  }

  saveChanges(): void {
    this.commit();
  }

  discardChanges(): void {
    this.gridStore.revertChanges();
  }

  commit() {
    super.commit();
    this.entityChange.emit();
  }

  onGridEvent(event: any): void {
    // Handle grid events if needed
    console.log("Grid event:", event);
  }
}
